import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import UtilitiesApi from '../../../../../apis/UtilitiesApi';
import { UtilitiesUserSearchDto } from '../../../../../generated-types/utilitiesUserSearchDto';
import useApi from '../../../../../lib/useApi';

/**
 * Properties for the user search component
 */
interface UserSearchProps {
  /**
   * A method that fires when a user is selected. Typically used with a useState
   * @param _ The selected user
   * @returns void
   */
  onSelectedUser: (_: UtilitiesUserSearchDto | null) => void;
  /**
   * The selected user. Typically used with a useState
   */
  selectedUser: UtilitiesUserSearchDto | null;
}

/**
 * A component that allows the admin to search all the users
 */
const UserSearch = ({ onSelectedUser, selectedUser }: UserSearchProps) => {
  const [users, setUsers] = useState<UtilitiesUserSearchDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const utilitiesApi = useApi(UtilitiesApi);

  /**
   * Load the users
   */
  useEffect(() => {
    utilitiesApi
      .GetUsers()
      .then(setUsers)
      .catch(() => {
        // TODO handle error
        setError(true);
      })
      .finally(() => setIsLoading(false));
  }, [utilitiesApi]);

  /**
   * Build the users full name
   */
  const buildFullName = useCallback(
    (user: UtilitiesUserSearchDto) => `${user.firstName} ${user.lastName}`,
    []
  );

  return error ? (
    <Box display="flex" margin="1em" gap=".5em" justifyContent="center">
      <ErrorOutlineRoundedIcon color="error" />
      <Typography>An error occurred while loading the users.</Typography>
    </Box>
  ) : (
    <Autocomplete
      disabled={isLoading}
      autoComplete
      options={users}
      getOptionLabel={(u) => {
        if (typeof u === 'string') {
          return u;
        }

        const organizationPart =
          u.organization != undefined ? `${u.organization} - ` : '';
        return `${buildFullName(u)} (${organizationPart}${u.email})`;
      }}
      value={selectedUser}
      onChange={(_, newSelectedUser) => {
        if (typeof newSelectedUser === 'string') return;
        onSelectedUser(newSelectedUser);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={isLoading ? '' : 'Search users'}
          disabled={isLoading}
          margin="normal"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            startAdornment: isLoading && (
              <>
                <CircularProgress
                  color="inherit"
                  size={20}
                  sx={{
                    marginRight: '1em',
                    marginLeft: '.5em',
                  }}
                />
                <Typography variant="body1">Loading users...</Typography>
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default UserSearch;
